import { Link } from "react-router-dom";
import type { CustomerDetailDto } from "@grizzly/shared";
import { customerStatus } from "@grizzly/shared";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Archive, ArrowLeft, Undo2 } from "lucide-react";

export function CustomerHeader({
  customer,
  busy,
  onToggleArchive,
}: {
  customer: CustomerDetailDto;
  busy: boolean;
  onToggleArchive(): void;
}) {
  const { t } = useI18n();
  const status = customerStatus(customer.status);
  const archived = customer.status === "archived";

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/customers">
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
            {t("customers.header.back")}
          </Link>
        </Button>
      </div>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">
            {customer.displayName || customer.legalName}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
            <span className="tabular">{customer.code}</span>
            <Badge tone={status.tone}>
              {t(`status.customer.${customer.status}` as MessageKey)}
            </Badge>
            {customer.phone || customer.email ? (
              <span>{[customer.phone, customer.email].filter(Boolean).join(" · ")}</span>
            ) : null}
          </p>
          {customer.notes ? (
            <p className="mt-2 max-w-prose text-sm text-ink-muted">{customer.notes}</p>
          ) : null}
        </div>
        <Button
          variant={archived ? "secondary" : "danger"}
          onClick={onToggleArchive}
          disabled={busy}
        >
          {archived ? (
            <Undo2 className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
          ) : (
            <Archive className="h-4 w-4" aria-hidden="true" />
          )}
          {archived ? t("customers.header.reactivate") : t("customers.header.archive")}
        </Button>
      </header>
    </>
  );
}
